import { useState, type FormEvent } from 'react';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { TextField } from '../../components/ui/TextField';
import './admin-auth.css';

/**
 * Admin sign-in. Uses the ordinary shared login flow; the admin role is
 * verified afterwards by AdminSessionProvider, so this screen never decides
 * who is an admin.
 *
 * `loginWithEmail` does not throw — it writes `error` on the auth state — and
 * the route guard redirects once `isAuthenticated` flips, so there is no
 * navigation in the submit handler.
 */
export function AdminLogin() {
  const { t } = useTranslation('admin');
  const { loginWithEmail, loading, error, clearError } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    identifier?: string;
    password?: string;
  }>({});

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: { identifier?: string; password?: string } = {};
    if (!identifier.trim()) {
      nextErrors.identifier = t('login.identifierRequired');
    }
    if (!password) {
      nextErrors.password = t('login.passwordRequired');
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    clearError();
    await loginWithEmail({ identifier: identifier.trim(), password });
  };

  return (
    <main className="admin-auth">
      <div className="admin-auth__card">
        <div className="admin-auth__panel">
          <div className="admin-auth__intro">
            <h1 className="admin-auth__title">{t('login.title')}</h1>
            <p className="admin-auth__subtitle">{t('login.subtitle')}</p>
          </div>
          <form className="admin-auth__form" onSubmit={onSubmit} noValidate>
            <FormError variant="banner" message={error} />
            <TextField
              label={t('login.identifierLabel')}
              placeholder={t('login.identifierPlaceholder')}
              value={identifier}
              autoComplete="username"
              autoFocus
              error={fieldErrors.identifier}
              onChange={event => setIdentifier(event.target.value)}
            />
            <div className="admin-auth__password">
              <TextField
                label={t('login.passwordLabel')}
                placeholder={t('login.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                autoComplete="current-password"
                error={fieldErrors.password}
                onChange={event => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="admin-auth__password-toggle"
                aria-pressed={showPassword}
                onClick={() => setShowPassword(visible => !visible)}>
                {showPassword ? t('login.hidePassword') : t('login.showPassword')}
              </button>
            </div>
            <Button type="submit" block disabled={loading}>
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
            <p className="admin-auth__hint">{t('login.roleHint')}</p>
          </form>
        </div>
        <aside className="admin-auth__brand">
          <BrandLogo />
          <p className="admin-auth__brand-caption">{t('app.subtitle')}</p>
          <p className="admin-auth__brand-note">{t('login.brandNote')}</p>
        </aside>
      </div>
    </main>
  );
}
